import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import Button from '../../atoms/Button';
import { GET_DEVICE_CONFIGURATION, UPSERT_DEVICE_CONFIGURATION, CREATE_DEVICE_COMMAND } from './queries';
import './DeviceCard.scss';

export default function DeviceConfigSection({ deviceId }) {
  // --- State hooks ---
  const [jsonText, setJsonText] = useState('{}');
  const [jsonError, setJsonError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // --- Apollo/GraphQL hooks ---
  const {
    data: configData,
    loading: configLoading,
    refetch: refetchConfig,
  } = useQuery(GET_DEVICE_CONFIGURATION, {
    variables: { deviceId },
    skip: !deviceId,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: (data) => {
      if (data?.device_configuration?.[0]?.data) {
        setJsonText(JSON.stringify(data.device_configuration[0].data, null, 2));
        setJsonError(null);
      }
    },
  });

  const [upsertConfig] = useMutation(UPSERT_DEVICE_CONFIGURATION, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: () => {
      setIsSaving(false);
      setSaveSuccess(true);
      refetchConfig();
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error) => {
      console.error('Failed to save device configuration:', error);
      setIsSaving(false);
      setJsonError('Failed to save configuration. Please try again.');
    },
  });

  const [createCommand] = useMutation(CREATE_DEVICE_COMMAND, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: () => {
      setIsSending(false);
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    },
    onError: (error) => {
      console.error('Failed to send configuration to device:', error);
      setIsSending(false);
      setJsonError('Failed to send configuration to device. Please try again.');
    },
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (configData?.device_configuration?.[0]?.data) {
      setJsonText(JSON.stringify(configData.device_configuration[0].data, null, 2));
      setJsonError(null);
    } else if (!configLoading && !configData?.device_configuration?.[0]) {
      // No existing config, start with empty object
      setJsonText('{}');
    }
  }, [configData, configLoading]);

  // --- Function definitions ---
  /**
   * Safely converts JavaScript object syntax to JSON string
   * Handles: unquoted keys, single quotes, trailing commas
   * Does NOT execute code - only performs string transformations
   * This is safer than using eval/Function constructor
   */
  const convertJsObjectToJson = (text) => {
    let jsonString = text.trim();

    // Step 1: Remove trailing commas before closing braces/brackets
    jsonString = jsonString.replace(/,(\s*[}\]])/g, '$1');

    // Step 2: Temporarily replace string literals to protect them
    const stringPlaceholders = [];
    let placeholderIndex = 0;

    // Match both single and double quoted strings, handling escaped quotes
    const stringRegex = /(['"])(?:(?=(\\?))\2.)*?\1/g;
    jsonString = jsonString.replace(stringRegex, (match) => {
      const placeholder = `__STRING_${placeholderIndex}__`;
      stringPlaceholders[placeholderIndex] = match;
      placeholderIndex++;
      return placeholder;
    });

    // Step 3: Add quotes around unquoted object keys
    // Match: identifiers that appear before a colon (not already quoted)
    jsonString = jsonString.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$-]*)\s*:/g, '$1"$2":');

    // Step 4: Convert single-quoted strings to double-quoted
    // Restore placeholders and convert single quotes to double quotes
    for (let i = 0; i < stringPlaceholders.length; i++) {
      const original = stringPlaceholders[i];
      let converted = original;
      if (original.startsWith("'") && original.endsWith("'")) {
        // Convert single quotes to double, but preserve escaped quotes
        converted = original
          .replace(/\\'/g, '__TEMP_ESC_SINGLE__')
          .replace(/'/g, '"')
          .replace(/__TEMP_ESC_SINGLE__/g, "\\'");
      }
      jsonString = jsonString.replace(`__STRING_${i}__`, converted);
    }

    return jsonString;
  };

  const parseConfig = (text) => {
    // First try standard JSON parsing
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null) {
        return { valid: false, error: 'Configuration must be a JSON object' };
      }
      return { valid: true, data: parsed };
    } catch (jsonError) {
      // If JSON parsing fails, try converting JS object syntax to JSON
      try {
        const jsonString = convertJsObjectToJson(text);
        const parsed = JSON.parse(jsonString);
        if (typeof parsed !== 'object' || parsed === null) {
          return { valid: false, error: 'Configuration must be a JSON object' };
        }
        return { valid: true, data: parsed };
      } catch (jsError) {
        return { valid: false, error: `Invalid configuration: ${jsError.message}` };
      }
    }
  };

  const validateJson = (text) => {
    return parseConfig(text);
  };

  const handleJsonChange = (e) => {
    const newText = e.target.value;
    setJsonText(newText);
    setJsonError(null);
    setSaveSuccess(false);
    setSendSuccess(false);

    // Validate JSON on change
    const validation = validateJson(newText);
    if (!validation.valid) {
      setJsonError(validation.error);
    }
  };

  const handleSave = async () => {
    const validation = validateJson(jsonText);
    if (!validation.valid) {
      setJsonError(validation.error);
      return;
    }

    setIsSaving(true);
    setJsonError(null);

    try {
      const now = Math.floor(Date.now() / 1000);
      await upsertConfig({
        variables: {
          deviceId,
          version: 1,
          startedAt: now,
          data: validation.data,
        },
      });
    } catch (error) {
      console.error('Error saving configuration:', error);
      setIsSaving(false);
      setJsonError('Failed to save configuration. Please try again.');
    }
  };

  const handleSendToDevice = async () => {
    const validation = validateJson(jsonText);
    if (!validation.valid) {
      setJsonError(validation.error);
      return;
    }

    setIsSending(true);
    setJsonError(null);

    try {
      // First save the configuration if it's different from current
      const currentConfig = configData?.device_configuration?.[0]?.data;
      const configString = JSON.stringify(validation.data);
      const currentConfigString = currentConfig ? JSON.stringify(currentConfig) : '{}';

      if (configString !== currentConfigString) {
        const now = Math.floor(Date.now() / 1000);
        await upsertConfig({
          variables: {
            deviceId,
            version: 1,
            startedAt: now,
            data: validation.data,
          },
        });
        // Refetch config after saving
        await refetchConfig();
      }

      // Then create the Set Configuration command
      await createCommand({
        variables: {
          deviceId,
          type: 'Set Configuration',
          data: {},
        },
      });
    } catch (error) {
      console.error('Error sending configuration to device:', error);
      setIsSending(false);
      setJsonError('Failed to send configuration to device. Please try again.');
    }
  };

  // --- Render ---
  return (
    <div className="DeviceCard-configSection">
      <div className="DeviceCard-configInput">
        <textarea
          className={`DeviceCard-configTextarea ${jsonError ? 'DeviceCard-configTextarea--error' : ''}`}
          value={jsonText}
          onChange={handleJsonChange}
          placeholder="Enter device configuration as JSON or JavaScript object syntax (e.g., { key: 'value' })..."
          rows={12}
          disabled={configLoading}
        />
        {jsonError && <div className="DeviceCard-configError">{jsonError}</div>}
        {saveSuccess && <div className="DeviceCard-configSuccess">Configuration saved successfully!</div>}
        {sendSuccess && <div className="DeviceCard-configSuccess">Configuration sent to device!</div>}
      </div>
      <div className="DeviceCard-configActions">
        <Button
          type="button"
          variant="vivid"
          content={isSaving ? 'Saving...' : 'Save'}
          onClick={handleSave}
          disabled={isSaving || isSending || !!jsonError || configLoading}
          loading={isSaving}
        />
        <Button
          type="button"
          variant="vivid"
          content={isSending ? 'Sending...' : 'Send to Device'}
          onClick={handleSendToDevice}
          disabled={isSaving || isSending || !!jsonError || configLoading}
          loading={isSending}
        />
      </div>
    </div>
  );
}

DeviceConfigSection.propTypes = {
  deviceId: PropTypes.string.isRequired,
};
